/**
 * Hook for auto-polling CloudWatch logs and DynamoDB job status
 * for in-progress jobs that don't have an active WebSocket connection.
 *
 * Uses setInterval (not recursive setTimeout) so the browser keeps a
 * consistent cadence even when the tab is backgrounded, and adds a
 * visibilitychange listener to poll immediately when the user returns.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { JobRun, AgentLogEntry } from '../types';
import { CloudWatchLogsService, ParsedLogEntry } from 'src/services/upgrade-medic/CloudWatchLogsService';
import { UpgradeMedicDataService } from 'src/services/upgrade-medic/UpgradeMedicDataService';

const CLOUDWATCH_POLL_INTERVAL_MS = 10000;

const mapLogs = (logs: ParsedLogEntry[]): AgentLogEntry[] =>
  logs.map((l) => ({
    id: Date.now() + Math.random(),
    timestamp: l.timestamp,
    level: l.level,
    message: l.message,
    stage: l.stage
  }));

export const useCloudWatchPolling = (
  isAgentPanelVisible: boolean,
  setAgentLogs: React.Dispatch<React.SetStateAction<AgentLogEntry[]>>,
  setJobHistory: React.Dispatch<React.SetStateAction<JobRun[]>>,
  setProgressMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  const [cloudWatchPollingJobId, setCloudWatchPollingJobId] = useState<string | null>(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);

  // Guard against overlapping polls (e.g. visibility + interval firing together)
  const isPollingInFlightRef = useRef(false);

  const poll = useCallback(
    async (jobId: string): Promise<'continue' | 'stop'> => {
      if (isPollingInFlightRef.current) return 'continue';
      isPollingInFlightRef.current = true;

      try {
        // Fetch all pages of logs from CloudWatch
        const logs = await CloudWatchLogsService.fetchAllJobLogs(jobId);
        if (logs.length > 0) {
          setAgentLogs(mapLogs(logs));
        }

        // Fetch job status from DynamoDB
        const updatedJob = await UpgradeMedicDataService.getJobRunById(jobId);
        if (updatedJob) {
          setJobHistory((prev) =>
            prev.map((job) =>
              job.jobId === jobId
                ? {
                    ...job,
                    currentStage: updatedJob.currentStage,
                    progress: updatedJob.progress,
                    progressMessage: updatedJob.progressMessage,
                    stages: updatedJob.stages,
                    artifacts: updatedJob.artifacts,
                    status: updatedJob.status,
                    completedAt: updatedJob.completedAt
                  }
                : job
            )
          );
          if (updatedJob.progressMessage) {
            setProgressMessage(`[Auto-refresh] ${updatedJob.progressMessage}`);
          }

          // Job finished — the logs fetched above are already the final state
          if (updatedJob.status === 'completed' || updatedJob.status === 'failed' || updatedJob.status === 'cancelled') {
            setProgressMessage(`Job ${updatedJob.status}: ${updatedJob.progressMessage || ''}`);
            return 'stop';
          }
        }
      } catch (error) {
        console.error('[Upgrade Medic] CloudWatch polling error:', error);
      } finally {
        isPollingInFlightRef.current = false;
      }
      return 'continue';
    },
    [setAgentLogs, setJobHistory, setProgressMessage]
  );

  // Main polling effect
  useEffect(() => {
    if (!cloudWatchPollingJobId) {
      setIsAutoRefreshing(false);
      return;
    }

    const jobId = cloudWatchPollingJobId;
    console.log(`[Upgrade Medic] Starting CloudWatch auto-polling for job: ${jobId}`);
    setIsAutoRefreshing(true);

    let stopped = false;

    const runPoll = async () => {
      if (stopped) return;
      const result = await poll(jobId);
      if (result === 'stop' && !stopped) {
        stopped = true;
        setCloudWatchPollingJobId(null);
        setIsAutoRefreshing(false);
      }
    };

    // Immediate first poll
    runPoll();

    // Interval-based polling (survives background tabs better than setTimeout chains)
    const intervalId = setInterval(runPoll, CLOUDWATCH_POLL_INTERVAL_MS);

    // Immediate poll when tab regains focus
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !stopped) {
        console.log('[Upgrade Medic] Tab visible — polling immediately');
        runPoll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      console.log(`[Upgrade Medic] Stopping CloudWatch polling for job: ${jobId}`);
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      setIsAutoRefreshing(false);
    };
  }, [cloudWatchPollingJobId, poll]);

  // Stop polling when agent panel is closed
  useEffect(() => {
    if (!isAgentPanelVisible && cloudWatchPollingJobId) {
      console.log('[Upgrade Medic] Agent panel closed, stopping CloudWatch polling');
      setCloudWatchPollingJobId(null);
    }
  }, [isAgentPanelVisible, cloudWatchPollingJobId]);

  return {
    cloudWatchPollingJobId,
    setCloudWatchPollingJobId,
    isAutoRefreshing
  };
};
